import React from "react";
import styles from "./ZodiacWheel.module.css";

interface ZodiacWheelProps {
  activeIndex?: number;
}

const ZodiacWheel: React.FC<ZodiacWheelProps> = ({ activeIndex }) => {
  const size = 360;
  const center = size / 2;
  const outerRadius = 170;
  const middleRadius = 95;
  const centerRadius = 30;
  const numSectors = 12;

  const zodiacIcons = [
    "aries",
    "taurus",
    "gemini",
    "cancer",
    "leo",
    "virgo",
    "libra",
    "scorpio",
    "sagittarius",
    "capricorn",
    "aquarius",
    "pisces",
  ];

  const zodiacNames = [
    "ОВЕН",
    "ТЕЛЕЦ",
    "БЛИНЕЦЫ",
    "РАК",
    "ЛЕВ",
    "ДЕВА",
    "ВЕСЫ",
    "СКОРПИОН",
    "СТРЕЛЕЦ",
    "КОЗЕРОГ",
    "ВОДОЛЕЙ",
    "РЫБЫ",
  ];

  const zodiacBigIcons = zodiacIcons.map((name) => `${name}-big`);

  const sectors = Array.from({ length: numSectors }, (_, i) => {
    const startAngle = (i * 360) / numSectors - 90;
    const endAngle = ((i + 1) * 360) / numSectors - 90;

    const x1 = center + outerRadius * Math.cos((startAngle * Math.PI) / 180);
    const y1 = center + outerRadius * Math.sin((startAngle * Math.PI) / 180);
    const x2 = center + outerRadius * Math.cos((endAngle * Math.PI) / 180);
    const y2 = center + outerRadius * Math.sin((endAngle * Math.PI) / 180);

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    const pathId = `sector-path-${i}`;
    const sectorPath = `
      M ${center} ${center}
      L ${x1} ${y1}
      A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}
      Z
    `;

    // <<<<<< Увеличенный радиус для текста >>>>>>
    const labelRadius = outerRadius + 5;
    const labelX1 = center + labelRadius * Math.cos((startAngle * Math.PI) / 180);
    const labelY1 = center + labelRadius * Math.sin((startAngle * Math.PI) / 180);
    const labelX2 = center + labelRadius * Math.cos((endAngle * Math.PI) / 180);
    const labelY2 = center + labelRadius * Math.sin((endAngle * Math.PI) / 180);

    const arcPath = `
      M ${labelX1} ${labelY1}
      A ${labelRadius} ${labelRadius} 0 ${largeArcFlag} 1 ${labelX2} ${labelY2}
    `;

    return (
      <g key={i}>
        <path
          d={sectorPath}
          fill={i === activeIndex ? "url(#sectorGradient)" : "transparent"}
          opacity={i === activeIndex ? 1 : 0.7}
        />
        <path id={pathId} d={arcPath} fill="none" />
        {i === activeIndex && (
          <text fill="#73E9FE" fontSize="10" fontWeight="bold">
            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
              {zodiacNames[i]}
            </textPath>
          </text>
        )}
      </g>
    );
  });

  const icons = zodiacIcons.map((name, i) => {
    const angle = (i * 360) / numSectors + 15 - 90;
    const rad = (angle * Math.PI) / 180;
    const radius = middleRadius - 30;
    const size = 20;
    const x = center + radius * Math.cos(rad) - size / 2;
    const y = center + radius * Math.sin(rad) - size / 2;

    return (
      <image
        key={`icon-${i}`}
        href={`/zodiac/${name}.svg`}
        x={x}
        y={y}
        width={size}
        height={size}
        className={styles.zodiacIcon}
      />
    );
  });

  const bigIcons = zodiacBigIcons.map((name, i) => {
    const angle = (i * 360) / numSectors + 15 - 90;
    const rad = (angle * Math.PI) / 180;
    const radius = outerRadius - 35;
    const size = 50;
    const x = center + radius * Math.cos(rad) - size / 2;
    const y = center + radius * Math.sin(rad) - size / 2;

    return (
      <image
        key={`big-icon-${i}`}
        href={`/zodiac/${name}.svg`}
        x={x}
        y={y}
        width={size}
        height={size}
        opacity="1"
      />
    );
  });

  return (
    <div className={styles.wrapper}>
      <svg className={styles.wheel} viewBox={`0 0 ${size} ${size}`}>
        {/* Подсветка сектора */}
        {sectors}

        {/* Кольца */}
        <circle
          cx={center}
          cy={center}
          r={middleRadius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth={1}
          fill="none"
        />
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          stroke="rgba(255,255,255,0.15)"
          strokeWidth={1}
          fill="none"
        />

        {/* Иконки */}
        {icons}
        {bigIcons}

        {/* Центр */}
        <circle cx={center} cy={center} r={centerRadius} fill="url(#centerGradient)" />
        <image
          href="/icons/Simbols.svg"
          x={center - 26}
          y={center - 26}
          width="52"
          height="52"
          opacity="0.9"
        />

        {/* Градиенты */}
        <defs>
          <linearGradient id="centerGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5b2a85" />
            <stop offset="100%" stopColor="#3b0a60" />
          </linearGradient>
          <linearGradient id="sectorGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#a66cff" />
            <stop offset="100%" stopColor="#8a62e6" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default ZodiacWheel;
